import type { PromptColumn } from '@/api/models/PromptColumn';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';


export type AgentsSelectorProps = {
  promptColumns: PromptColumn[] | undefined;
  agents: any;
  setAgents: (agents: any) => void;
};

interface CheckboxTreeProps {
  data: {
    [key: string]: {
      id: string;
      group: string;
      prompt: string;
      name: string;
      rank: string;
      numberRank: number;
    }[];
  };
}
export default function AgentsSelector({
  promptColumns,
  agents,
  setAgents,
}: AgentsSelectorProps) {
  const [groupedPromptColumns, setGroupedPromptColumns] = useState<any>({});

  function sortDataAndRankAccordingly(data: PromptColumn[] | undefined) {
    data = data?.sort((a, b) => a.rank.localeCompare(b.rank));

    let currentGroup: any = null;
    let rankCounter = 0;
    const newData = data?.map((obj, index) => {
      if (obj?.group !== currentGroup) {
        currentGroup = obj.group;
        rankCounter = 1;
      } else {
        rankCounter++;
      }
      return {
        ...obj,
        numberRank: rankCounter,
      };
    });

    return newData;
  }

  function groupDataByGroupKey(
    data: PromptColumn[] | undefined,
  ): Record<string, PromptColumn[]> {
    const groupedData: Record<string, PromptColumn[]> = {};

    data = sortDataAndRankAccordingly(data);

    if (data) {
      data.forEach(item => {
        const group = item.group;
        if (group) {
          if (!groupedData[group]) {
            groupedData[group] = [];
          }
          groupedData[group].push(item);
        }
      });

      return groupedData;
    }
    return {};
  }

  const handleParentCheckboxChange = (parentKey: string, checked: boolean) => {
    const updatedCheckedItems = { ...agents, [parentKey]: checked };

    // Update all children checkboxes accordingly
    groupedPromptColumns[parentKey].forEach((child: any) => {
      updatedCheckedItems[child.id] = checked;
    });

    setAgents(updatedCheckedItems);
  };

  const handleChildCheckboxChange = (childKey: string, checked: boolean) => {
    // find the object with childkey
    const foundObject = sortDataAndRankAccordingly(promptColumns)?.find(
      obj => obj.id === childKey,
    );

    if (checked === true) {
      // find the object with previous rank
      const foundObjectWithPreviousRank = sortDataAndRankAccordingly(
        promptColumns,
      )?.find(
        obj =>
          obj.group === foundObject?.group &&
          obj.numberRank === foundObject?.numberRank! - 1,
      );
      if (foundObject?.numberRank === 1) {
        const updatedCheckedItems = { ...agents, [childKey]: checked };

        // Check if all children of the parent are checked, then check the parent
        Object.keys(groupedPromptColumns).forEach(parentKey => {
          const allChildrenChecked = groupedPromptColumns[parentKey].every(
            (child: any) => updatedCheckedItems[child.id],
          );
          updatedCheckedItems[parentKey] = allChildrenChecked;
        });

        setAgents(updatedCheckedItems);
      } else {
        // check if id of foundObjectWithPreviousRank is present in checkedItems and is true
        if (agents[foundObjectWithPreviousRank?.id!]) {
          const updatedCheckedItems = { ...agents, [childKey]: checked };

          // Check if all children of the parent are checked, then check the parent
          Object.keys(groupedPromptColumns).forEach(parentKey => {
            const allChildrenChecked = groupedPromptColumns[parentKey].every(
              (child: any) => updatedCheckedItems[child.id],
            );
            updatedCheckedItems[parentKey] = allChildrenChecked;
          });

          setAgents(updatedCheckedItems);
        } else {
          // show a popup
          toast(`Cannot select ${foundObject?.name}`, {
            description: `${foundObject?.name} is dependent on ${foundObjectWithPreviousRank?.name}, please select ${foundObjectWithPreviousRank?.name} first.`,
            duration: 5000,
          });
        }
      }
    } else {
      // find the object with next rank
      const foundObjectsWithNextRanks = sortDataAndRankAccordingly(
        promptColumns,
      )?.filter(
        obj =>
          obj.group === foundObject?.group &&
          obj.numberRank > foundObject?.numberRank!,
      );

      // check if id of foundObjectsWithNextRank is present in checkedItems and is true
      // and for each item of foundObjectsWithNextRank, check if it is present in checkedItems if present, make it false,
      // and if not present add that item and make it false
      let updatedCheckedItems: any = { ...agents };
      foundObjectsWithNextRanks?.forEach(item => {
        updatedCheckedItems = { ...updatedCheckedItems, [item.id]: checked };
      });

      updatedCheckedItems = { ...updatedCheckedItems, [childKey]: checked };

      // Check if all children of the parent are checked, then check the parent
      Object.keys(groupedPromptColumns).forEach(parentKey => {
        const allChildrenChecked = groupedPromptColumns[parentKey].every(
          (child: any) => updatedCheckedItems[child.id],
        );
        updatedCheckedItems[parentKey] = allChildrenChecked;
      });

      setAgents(updatedCheckedItems);

      if (foundObjectsWithNextRanks!?.length > 0) {
        toast(`Dependent Agents are removed!`, {
          description: `Your current unselection has removed any dependent agents.`,
          duration: 5000,
        });
      }
    }
  };

  useEffect(() => {
    setGroupedPromptColumns(groupDataByGroupKey(promptColumns));
  }, [promptColumns]);

  return (
    <>
      {Object.keys(groupedPromptColumns).map(parentKey => (
        <div key={parentKey} className="mb-3">
          <div className="flex items-center space-x-2 rounded-md p-2">
            <Checkbox
              checked={agents[parentKey] || false}
              onCheckedChange={(checked: boolean) =>
                handleParentCheckboxChange(parentKey, checked)
              }
              className="form-checkbox h-4 w-4 text-indigo-600"
            />
            <label
              htmlFor={parentKey}
              className={cn("text-base font-semibold")}
            >
              {parentKey}
            </label>
          </div>

          <div className="ml-8 mt-2 space-y-3">
            {groupedPromptColumns[parentKey].map((child: any) => (
              <div key={child.id} className="flex items-center space-x-3">
                <Checkbox
                  value={child.id}
                  checked={agents[child.id] || false}
                  onCheckedChange={(checked: boolean) =>
                    handleChildCheckboxChange(child.id, checked)
                  }
                  className="form-checkbox h-4 w-4 text-indigo-600"
                />
                <label
                  htmlFor={child.name}
                  className="text-sm font-medium "
                >
                  {child.name}
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}
